import type { TourEngineFrame, TourFrame } from '@carboncopyco/types';
import { List, Spacer, TextCaption } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useTourSelector } from '../../hooks/store';
import { useScrollVisibilityHandler } from '../../hooks/useScrollVisibilityHandler';
import { selectFramesLoading } from '../../store';
import { useTourCapturePageProps } from './TourCapturePageContext';
import { TourFrameCaptureItem } from './TourFrameCaptureItem';

interface TourCaptureListProps {
  frames?: TourEngineFrame[];
  onScrollVisibleChange: (boolean) => void;
}

export const TourCaptureList = ({
  frames,
  onScrollVisibleChange,
}: TourCaptureListProps) => {
  const { uuid } = useTourCapturePageProps();
  const framesLoading = useTourSelector(selectFramesLoading(uuid));
  const refScrollElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentObjElement = refScrollElement.current;
    if (currentObjElement) {
      return () => {
        currentObjElement.scrollTop = currentObjElement.scrollHeight;
      };
    }
  }, [frames, refScrollElement]);

  useScrollVisibilityHandler(refScrollElement, onScrollVisibleChange);

  return (
    <div
      className="h-full flex flex-col overflow-hidden justify-between"
      data-testid="tour-capture-list"
    >
      <div
        ref={refScrollElement}
        className={classNames(
          'h-full flex flex-col overflow-hidden overflow-y-auto p-3 flex-grow',
          { 'shadow-sm': frames?.length > 5 }
        )}
      >
        {frames?.length === 0 && (
          <div className="flex justify-center items-center border border-dashed rounded-md py-8">
            <TextCaption
              label="First frame will appear here"
              color="text-ds-natural-200"
            />
          </div>
        )}
        <List<TourFrame>
          data={frames}
          renderItem={(frame, __, _, index) => (
            <TourFrameCaptureItem
              key={`frame${index}`}
              frameData={frame}
              isLoading={framesLoading[index]?.isLoading}
              progress={framesLoading[index]?.progress}
            />
          )}
        />
        <Spacer amount={1.25} />
      </div>
    </div>
  );
};
